/**
 * Fixed width every chart's Y-axis column was built against. Sized for 16px
 * ticks (the global font-size control's default) on a desktop-width card.
 */
export const CHART_Y_AXIS_WIDTH = 72;

/** Approximate advance width of a digit / `$` / `,` as a fraction of the em. */
const TICK_GLYPH_EM = 0.6;
/** Room for the tick line plus its gap to the label. */
const TICK_GUTTER_PX = 8;
/** Never reserve less than this — below it even short labels start clipping. */
const MIN_Y_AXIS_WIDTH = 34;
/** `$XX.XX` — the shortest label we're willing to size the axis against. */
const MIN_TICK_LABEL_CHARS = 6;

/**
 * Width to reserve for a chart's Y-axis column.
 *
 * Desktop keeps the fixed {@link CHART_Y_AXIS_WIDTH} so nothing shifts. On
 * phones that 72px is mostly padding: ticks render at ~12px there (see
 * `scaleChartFontSizeForPhone`), so a label like `$58.66` needs ~44px and the
 * remainder shows up as dead space between the card edge and the plot — ~7% of
 * a 390px screen. Recharts 2.x has no `width="auto"` (that landed in 3.0), so
 * estimate from the widest label instead.
 *
 * `labels` should be the formatted domain endpoints. Ticks sit between them, so
 * their labels are no wider — except when a round-numbered domain (e.g. 0-100,
 * endpoints `$0`/`$100`) yields fractional ticks like `$16.67`. The
 * {@link MIN_TICK_LABEL_CHARS} floor covers that case.
 *
 * @param {object}   opts
 * @param {boolean}  opts.isPhone
 * @param {number}   opts.fontSize - tick font size actually being rendered
 * @param {string[]} [opts.labels] - formatted tick labels to measure against
 */
export function getChartYAxisWidth({ isPhone, fontSize, labels } = {}) {
  if (!isPhone) return CHART_Y_AXIS_WIDTH;

  const chars = (labels || []).reduce(
    (max, label) => Math.max(max, String(label ?? '').length),
    MIN_TICK_LABEL_CHARS
  );
  const estimated = Math.ceil(chars * fontSize * TICK_GLYPH_EM) + TICK_GUTTER_PX;

  return Math.min(CHART_Y_AXIS_WIDTH, Math.max(MIN_Y_AXIS_WIDTH, estimated));
}

/**
 * Format a Recharts `domain` into label strings for {@link getChartYAxisWidth}.
 * Non-numeric entries (`'auto'`, `'dataMin'`) are dropped — there's nothing to
 * measure, and the char floor takes over.
 */
export function getYAxisDomainLabels(domain, format) {
  if (!Array.isArray(domain)) return [];
  return domain
    .filter((v) => Number.isFinite(Number(v)))
    .map((v) => format(Number(v)));
}

/**
 * @param {object}  [opts]
 * @param {boolean} [opts.scrollable] - the legend manages its own height (a
 *   `maxHeight` + `overflowY` scroll box). Stamps the marker the mobile
 *   legend-wrap rule in index.css excludes, so below `lg` the wrapper keeps its
 *   own bounded, clipping box instead of being forced to `height: auto;
 *   overflow: visible` — which spills a tall legend out over the cards below.
 */
export function getLegendWrapperStyle({
  fontSize,
  paddingTop = 12,
  bottom = 0,
  left = 20,
  right = 20,
  position = 'absolute',
  scrollable = false,
  ...overrides
} = {}) {
  return {
    ...(fontSize != null ? { fontSize } : {}),
    paddingTop,
    position,
    left,
    right,
    bottom,
    ...(scrollable ? { '--chart-legend-scroll': 1 } : {}),
    ...overrides,
  };
}

